import Headroom from '@integreat-app/react-sticky-headroom'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import React, { ReactElement, ReactNode } from 'react'

import { REGIONS_ROUTE, pathnameFromRouteInformation } from 'shared'

import useElementRect from '../hooks/useElementRect'
import useUpdateDimensions from '../hooks/useUpdateDimensions'
import HeaderLogo from './HeaderLogo'
import HeaderTitle from './HeaderTitle'

const HEADER_HEIGHT = 80

export const HeaderContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.accent,
  backgroundImage: 'none',
  // Hide the top elevation shadow when overscrolling
  clipPath: 'inset(0 0 -16px 0)',
}))

const HeaderComponent = styled('header')`
  user-select: none;

  ${props => props.theme.breakpoints.up('lg')} {
    padding-inline: calc((100vw - ${props => props.theme.breakpoints.values.lg}px) / 2)
      calc((200% - 100vw - ${props => props.theme.breakpoints.values.lg}px) / 2);
  }

  ${props => props.theme.breakpoints.up('md')} {
    margin-inline-start: 80px;
  }
`

const Row = styled('div')`
  display: flex;
  align-items: center;
  min-height: ${HEADER_HEIGHT}px;
  justify-content: space-between;
  overflow-x: auto;
  padding: 0 16px;
  gap: 8px;

  ${props => props.theme.breakpoints.down('md')} {
    padding: 0 8px;
  }
`

const ActionBar = styled('nav')`
  order: 3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  ${props => props.theme.breakpoints.down('md')} {
    order: 2;
  }
`

type HeaderProps = {
  actionItems: ReactNode[]
  logoHref: string
  regionName?: string
  language: string
  tabBar?: ReactNode
}

export const Header = ({ actionItems = [], logoHref, regionName, language, tabBar }: HeaderProps): ReactElement => {
  const { rect: headerRect, ref } = useElementRect()
  useUpdateDimensions()

  const height = headerRect?.height ?? 0
  const regionsPath = pathnameFromRouteInformation({ route: REGIONS_ROUTE, languageCode: language })

  return (
    <Headroom scrollHeight={HEADER_HEIGHT} height={height} zIndex={2}>
      <HeaderContainer elevation={2}>
        <HeaderComponent ref={ref}>
          <Row>
            <Stack direction='row-reverse' sx={{ alignItems: 'center', gap: 1 }}>
              <HeaderLogo link={logoHref} />
              {!!regionName && <HeaderTitle title={regionName} regionsPath={regionsPath} />}
            </Stack>
            <ActionBar>{actionItems}</ActionBar>
          </Row>
          {tabBar}
        </HeaderComponent>
      </HeaderContainer>
    </Headroom>
  )
}

export default Header
